/**
 * Shared responsive design composable - singleton pattern
 * Promotes useResponsive from payroll module to shared composables
 */

import { ref, computed, onMounted, onUnmounted, type Ref } from 'vue'

export interface BreakpointConfig {
  xs: number
  sm: number
  md: number
  lg: number
  xl: number
  xxl: number
}

export interface ResponsiveState {
  width: Ref<number>
  height: Ref<number>
  breakpoint: Ref<string>
  isMobile: Ref<boolean>
  isTablet: Ref<boolean>
  isDesktop: Ref<boolean>
  orientation: Ref<'portrait' | 'landscape'>
  pixelRatio: Ref<number>
}

// Singleton state - one resize listener for the whole app
let singletonState: ResponsiveState | null = null
let listenerCount = 0
let updateDimensions: (() => void) | null = null

const DEFAULT_BREAKPOINTS: BreakpointConfig = {
  xs: 480,
  sm: 768,
  md: 1024,
  lg: 1280,
  xl: 1440,
  xxl: 1920,
}

function createResponsiveState(breakpoints: BreakpointConfig): ResponsiveState {
  const width = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)
  const height = ref(typeof window !== 'undefined' ? window.innerHeight : 768)

  const breakpoint = computed(() => {
    const w = width.value
    if (w < breakpoints.xs) return 'xs'
    if (w < breakpoints.sm) return 'sm'
    if (w < breakpoints.md) return 'md'
    if (w < breakpoints.lg) return 'lg'
    if (w < breakpoints.xl) return 'xl'
    return 'xxl'
  })

  const isMobile = computed(() => ['xs', 'sm'].includes(breakpoint.value))
  const isTablet = computed(() => breakpoint.value === 'md')
  const isDesktop = computed(() => ['lg', 'xl', 'xxl'].includes(breakpoint.value))

  const orientation = computed((): 'portrait' | 'landscape' =>
    height.value > width.value ? 'portrait' : 'landscape'
  )

  const pixelRatio = ref(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1)

  updateDimensions = () => {
    width.value = window.innerWidth
    height.value = window.innerHeight
  }

  return {
    width,
    height,
    breakpoint,
    isMobile,
    isTablet,
    isDesktop,
    orientation,
    pixelRatio,
  }
}

export function useResponsive(config?: Partial<BreakpointConfig>): ResponsiveState {
  const breakpoints = { ...DEFAULT_BREAKPOINTS, ...config }

  if (!singletonState) {
    singletonState = createResponsiveState(breakpoints)
  }

  onMounted(() => {
    if (listenerCount === 0 && updateDimensions) {
      window.addEventListener('resize', updateDimensions, { passive: true })
      window.addEventListener('orientationchange', updateDimensions, { passive: true })
    }
    listenerCount++
  })

  onUnmounted(() => {
    listenerCount--
    if (listenerCount === 0 && updateDimensions) {
      window.removeEventListener('resize', updateDimensions)
      window.removeEventListener('orientationchange', updateDimensions)
    }
  })

  return singletonState
}
